import { useCallback, useEffect, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import { useShoppingCartApi } from "@/hooks/apiHooks/useShoppingCartApi";
import { shoppingCartAtom } from "@/store/shoppingCartAtoms";
import type { ShoppingCartItem } from "@models/shoppingCart";
import {
    cartRefusal,
    stockLimitMessage,
    type LineRefusal,
} from "../../_helpers/stockCopy";

const QUANTITY_DEBOUNCE_MS = 400;

interface CartItemMutations {
    changeQuantity: (item: ShoppingCartItem, next: number) => void;
    removeItem: (item: ShoppingCartItem) => void;
    /**
     * True while any line has a quantity edit the server has not confirmed —
     * armed behind the debounce OR on the wire. Checkout gates Place Order on
     * it: CreateOrder reads the SERVER cart, so an order placed inside this
     * window ships the number the shopper edited away from.
     */
    hasPendingEdits: boolean;
    /**
     * Drops every armed edit without sending it. For the moment the cart
     * stops existing — a placed order — where the unmount flush below would
     * PATCH items CreateOrder just deleted and toast a 404 over "confirmed".
     */
    discardPending: () => void;
    /**
     * Why the server refused a line's last quantity change, by cart item id
     * (#443). The coded 400s — the vendor can't supply that many, the
     * product or offering is gone, or the line would pass the per-line limit —
     * are not toasted, so every page using this hook shows them on the line. A
     * line's next edit clears it.
     */
    quantityRefusals: Record<string, LineRefusal>;
    /** Drops a line's quantity refusal once the line changes some other way (a vendor switch). */
    clearQuantityRefusal: (itemId: string) => void;
}

/**
 * Persists cart row edits, keeping the optimistic atom write and the
 * revert-on-rejection here rather than in `Cart.tsx`.
 *
 * Local list operations stay keyed on `vendorSkuId` — the same product from two
 * vendors is two distinct lines (#305) — while the API takes the cart *item* id.
 */
export const useCartItemMutations = (options?: {
    /**
     * Called with the line's cart item id once its quantity edit is saved (#443:
     * Checkout unmarks that line of a stock 412 when the shopper fixes it on the
     * page itself). Not for an edit a newer one superseded, nor after unmount.
     */
    onEditSaved?: (itemId: string) => void;
}): CartItemMutations => {
    const { updateCartItemQuantity, removeCartItem } = useShoppingCartApi();
    const setShoppingCart = useSetAtom(shoppingCartAtom);
    // Read at settle time, so the caller's callback need not be stable.
    const onEditSavedRef = useRef(options?.onEditSaved);
    useEffect(() => {
        onEditSavedRef.current = options?.onEditSaved;
    });

    // Keyed by cart item id. `lastConfirmed` holds the quantity from BEFORE the
    // current burst so a rejection reverts the whole burst, not one keystroke.
    // All three MUST stay per-item: one shared slot would let an edit on one
    // line cancel a pending send on another.
    const timersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
    const flushesRef = useRef(new Map<string, () => void>());
    const lastConfirmedRef = useRef(new Map<string, number>());
    const isMountedRef = useRef(true);
    // The ids with an unconfirmed edit, mirrored into state as a count so a
    // consumer re-renders when the set changes. The ref is the truth; the
    // state is the signal.
    const pendingIdsRef = useRef(new Set<string>());
    const [pendingCount, setPendingCount] = useState(0);
    const [quantityRefusals, setQuantityRefusals] = useState<
        Record<string, LineRefusal>
    >({});

    const clearQuantityRefusal = useCallback((itemId: string) => {
        setQuantityRefusals((prev) => {
            if (!(itemId in prev)) return prev;
            const rest = { ...prev };
            delete rest[itemId];
            return rest;
        });
    }, []);

    const setPending = useCallback((itemId: string, pending: boolean) => {
        if (pending) {
            pendingIdsRef.current.add(itemId);
        } else {
            pendingIdsRef.current.delete(itemId);
        }
        if (isMountedRef.current) setPendingCount(pendingIdsRef.current.size);
    }, []);

    const setLineQuantity = useCallback(
        (vendorSkuId: string, quantity: number) => {
            setShoppingCart((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    items: prev.items.map((line) =>
                        line.vendorSkuId === vendorSkuId
                            ? { ...line, quantity }
                            : line,
                    ),
                };
            });
        },
        [setShoppingCart],
    );

    const changeQuantity = useCallback(
        (item: ShoppingCartItem, next: number) => {
            const itemId = item.id;

            if (!lastConfirmedRef.current.has(itemId)) {
                lastConfirmedRef.current.set(itemId, item.quantity);
            }

            // Optimistic: the stepper has to stay responsive under a fast burst.
            setLineQuantity(item.vendorSkuId, next);
            setPending(itemId, true);
            // A new edit is a new question; the last refusal answered the old one.
            clearQuantityRefusal(itemId);

            // Debounced (trailing edge). A `+ + +` burst would otherwise fire
            // three PATCHes whose responses can land out of order, and each
            // PATCH *sets* rather than increments, so the last response to
            // arrive would win and could persist a stale number. Collapsing the
            // burst to a single trailing request removes the race by
            // construction.
            const pending = timersRef.current.get(itemId);
            if (pending) clearTimeout(pending);

            const send = () => {
                timersRef.current.delete(itemId);
                flushesRef.current.delete(itemId);

                // A newer edit queued behind this request owns the outcome of
                // the line: `lastConfirmed` is ITS revert source too, so this
                // request must neither clear it nor revert on top of the newer
                // optimistic value. A queued edit is exactly `timersRef` still
                // holding an entry for this id once the request has settled.
                const supersededByQueuedEdit = () =>
                    timersRef.current.has(itemId);

                void updateCartItemQuantity(itemId, next)
                    .then(() => {
                        if (supersededByQueuedEdit()) return;

                        lastConfirmedRef.current.delete(itemId);
                        if (isMountedRef.current) {
                            onEditSavedRef.current?.(itemId);
                        }
                    })
                    .catch((error: unknown) => {
                        if (supersededByQueuedEdit()) return;

                        // A coded 400 — the vendor can't supply that many, the
                        // product or offering is gone, or the per-line limit —
                        // is said on the line (#443, PR 339); `useApi` toasted
                        // anything else (validation, 404, 5xx, …) but a 403,
                        // which the cart hook toasted itself or answered by
                        // moving the customer to a wall (#368). Either way
                        // the line goes back to its last valid value so the
                        // shopper never sees a phantom number.
                        const lastConfirmed =
                            lastConfirmedRef.current.get(itemId);
                        lastConfirmedRef.current.delete(itemId);

                        if (!isMountedRef.current) return;

                        const refusal = cartRefusal(error, ({ available }) =>
                            stockLimitMessage(available),
                        );
                        if (refusal) {
                            setQuantityRefusals((prev) => ({
                                ...prev,
                                [itemId]: refusal,
                            }));
                        }

                        if (lastConfirmed === undefined) return;

                        setLineQuantity(item.vendorSkuId, lastConfirmed);
                    })
                    .finally(() => {
                        // Settled either way — unless a newer edit on this line
                        // is queued, in which case it is still pending and
                        // that request clears it.
                        if (!supersededByQueuedEdit()) setPending(itemId, false);
                    });
            };

            flushesRef.current.set(itemId, send);
            timersRef.current.set(
                itemId,
                setTimeout(send, QUANTITY_DEBOUNCE_MS),
            );
        },
        [setLineQuantity, setPending, updateCartItemQuantity, clearQuantityRefusal],
    );

    const discardPending = useCallback(() => {
        timersRef.current.forEach((timer) => clearTimeout(timer));
        timersRef.current.clear();
        flushesRef.current.clear();
        lastConfirmedRef.current.clear();
        pendingIdsRef.current.clear();
        if (isMountedRef.current) setPendingCount(0);
    }, []);

    // Removal is a single deliberate click, so it is neither optimistic nor
    // debounced: the line disappears only once the server has accepted it.
    const removeItem = useCallback(
        (item: ShoppingCartItem) => {
            // Drop any quantity edit still pending on this line. Left armed it
            // would PATCH an id the DELETE just removed, raising a 404 toast
            // moments after an action the shopper watched succeed.
            const pending = timersRef.current.get(item.id);
            if (pending) clearTimeout(pending);
            timersRef.current.delete(item.id);
            flushesRef.current.delete(item.id);
            lastConfirmedRef.current.delete(item.id);
            setPending(item.id, false);

            void removeCartItem(item.id)
                .then(() => {
                    setShoppingCart((prev) => {
                        if (!prev) return prev;
                        return {
                            ...prev,
                            items: prev.items.filter(
                                (line) =>
                                    line.vendorSkuId !== item.vendorSkuId,
                            ),
                        };
                    });
                })
                .catch(() => {
                    /* cart untouched; useApi already toasted the failure, or
                       for a 403 the cart hook toasted it or moved the
                       customer to a wall (#368) */
                });
        },
        [removeCartItem, setPending, setShoppingCart],
    );

    useEffect(() => {
        isMountedRef.current = true;
        const timers = timersRef.current;
        const flushes = flushesRef.current;

        return () => {
            isMountedRef.current = false;
            // Flush rather than drop: a dropped edit would leave the server on
            // the old quantity, which checkout then re-reads.
            //
            // ORDER IS LOAD-BEARING — do not move the flush above the clear.
            // `send`'s settle handlers read `timersRef` to decide whether a
            // newer edit superseded them. Clearing the map first leaves that
            // check correctly false for every flushed request, so an unmount
            // flush still confirms or reverts normally. Flushing first would
            // leave each entry present and make every flush look superseded.
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
            flushes.forEach((flush) => flush());
            flushes.clear();
        };
    }, []);

    return {
        changeQuantity,
        removeItem,
        hasPendingEdits: pendingCount > 0,
        discardPending,
        quantityRefusals,
        clearQuantityRefusal,
    };
};
